import SurfaceRenderer from '../../renderers/SurfaceRenderer/index';
import {validateObject, error, cacheSet, cacheGet, getAssetKey, log} from "../../utils/utils";
import {productsServiceURL} from "../../utils/globals";

/**
 * @class SurfaceWrapper
 */
export default class SurfaceWrapper {

    debug = false;
    layoutData = {};
    sizeId = null;
    brand = "SFLY";
    occasionId = null;
    styleId = null;
    layoutName = null;
    size = "small";
    photos = [];
    photoAssignment = [];
    env = 'prod';
    hasTextData = false;
    isMagicShop = false;
    isMlsdk = false;

    /**
     * Constructing a new instance of SurfaceWrapper
     * @memberOf SurfaceWrapper
     * @param {SurfaceWrapperInput} params
     * @example
     * const wrapper = new SurfaceWrapper( {
     *     env: 'prod',
           sizeId: 122,
           styleId: 3771,
           occasionId: 38,
           layoutName: '238_L_18x24-pillow_99_sfly_3771_enjoy-little-things_park-night_S1_a_5',
           photos: [
               {
                   id: "1",
                   url: "https://uniim1.shutterfly.com/ng/services/mediarender/THISLIFE/022064442434/media/34558171300/medium/1558338774/enhance"
               },
               {
                   id: "2",
                   url: "https://uniim1.shutterfly.com/ng/services/mediarender/THISLIFE/022064442434/media/34558171319/medium/1558338777/enhance"
               },
               {
                   id: "3",
                   url: "https://uniim1.shutterfly.com/ng/services/mediarender/THISLIFE/022064442434/media/34558171334/medium/1558338778/enhance"
               },
               {
                   id: "4",
                   url: "https://uniim1.shutterfly.com/ng/services/mediarender/THISLIFE/022064442434/media/34558170065/medium/1558338455/enhance"
               },
               {
                   id: "5",
                   url: "https://uniim1.shutterfly.com/ng/services/mediarender/THISLIFE/022064442434/media/34558170060/medium/1558338454/enhance"
               }
           ],
           photoAssignment: [
               {
                   photoId: "1",
                   crop: [
                       0,
                       0,
                       1,
                       1
                   ],
                   imageAreaId: "1.2.2"
               },
               {
                   photoId: "2",
                   crop: [
                       0,
                       0,
                       1,
                       1
                   ],
                   imageAreaId: "1.2.4"
               },
               {
                   photoId: "3",
                   crop: [
                       0,
                       0,
                       1,
                       1
                   ],
                   imageAreaId: "1.2.6"
               }
           ]} );
     */
    constructor(params) {
        Object.assign(this, params);
        this.validate();

        if (this.debug) log(`initiated SurfaceWrapper`, this);
    }

    /**
     * Validates the initial input (sizeIdm occasionId, styleId, photos, photoAssignment)
     * @private
     * @memberOf SurfaceWrapper
     */
    validate() {
        if (!this.sizeId) {
            error("Validation Error", "sizeId is not a number");
        }

        if (!this.isMlsdk && !this.occasionId) {
            error("Validation Error", "occasionId is not a number");
        }

        if (!this.styleId) {
            error("Validation Error", "styleId is not a number");
        }

        if (!Array.isArray(this.photos)) {
            error("Validation Error", "photos is not an array");
        } else {
            if (this.photos.length > 0) {
                this.photos.forEach((photo, index) => {
                    if (typeof photo !== "object") {
                        error("Validation Error",
                            `photo at index ${index} is not an object`);
                    } else {
                        validateObject(`photos - at index ${index}`, photo, ["id", "url"])
                    }
                })
            }
        }

        if (!Array.isArray(this.photoAssignment)) {
            error("Validation Error", "photoAssignment is not an array");
        } else {
            if (this.photoAssignment.length > 0) {
                this.photoAssignment.forEach((photo, index) => {
                    if (typeof photo !== "object") {
                        error("Validation Error",
                            `photo at index ${index} is not an object`);
                    } else {
                        validateObject(`photoAssignment - at index ${index}`, photo, ["photoId", "crop", "imageAreaId"])
                    }
                })
            }
        }
    }

    /**
     * Generates the surface
     * @public
     * @memberOf SurfaceWrapper
     * @param {Boolean} ignoreTexts - if to ignore texts
     * @example
     * wrapper.generate().then(canvas=>{
     *     console.log(canvas);
     * })
     */
    async generate(ignoreTexts = false) {
        try {
            const data = this.layoutData;

            try {
                const generated = {
                    hasTextData: this.hasTextData,
                    size: this.size,
                    env: this.env,
                    isMagicShop: this.isMagicShop,
                    surface: {
                        id: data.id,
                        name: data.name,
                        // In mlsdk we use embellishmentAreas instead of surface layer
                        surfaceUrl: this.isMlsdk ? undefined : data.thumbs[this.size],
                        canvasWidth: data.printableAreaWidth * 72,
                        canvasHeight: data.printableAreaHeight * 72,
                        imageAreas: data.imageAreas,
                        embellishmentAreas: data.embellishmentAreas,
                        textAreas: data.textAreas?.map(textArea => Object.assign(textArea, {text: ignoreTexts ? "" : (textArea.text || "")})),
                        foilAreas: data.foilAreas || [],
                        backgroundUrl: this.isMlsdk ? data.backgroundUrl : data.backgroundImages && data.backgroundImages[this.size] ? data.backgroundImages[this.size] : ''
                    },
                    photos: this.photos,
                    photoAssignment: this.photoAssignment,
                    printableAreaX: data.printableAreaX * 72,
                    printableAreaY: data.printableAreaY * 72,
                    isMlsdk: this.isMlsdk,
                };
                const renderer = new SurfaceRenderer(generated);

                try {
                    return await renderer.render();
                } catch (e) {
                    error("Rendering", `Failed to render - ${e}`);
                }

            } catch (e) {
                error("SurfaceWrapper:: Generate", `failed to generate & render surface - ${e.message}`);
            }

        } catch (e) {
            error("SurfaceWrapper", `failed to get and process layout data - ${e.message}`);
        }
    }
}
